import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Trash, UploadSimple, Eye, ArrowSquareOut, Warning, CursorClick } from "@phosphor-icons/react";

const BANNER_W = 728;
const BANNER_H = 90;

export default function MyBanners() {
  const [banners, setBanners] = useState([]);
  const [balance, setBalance] = useState(0);
  const [limit, setLimit] = useState(3);
  const [membership, setMembership] = useState("Free");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [targetUrl, setTargetUrl] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/banners/mine");
      setBanners(data.banners || []);
      setBalance(Number(data.balance || 0));
      setLimit(Number(data.limit || 3));
      setMembership(data.membership || "Free");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const atLimit = banners.length >= limit;

  const validateAndUpload = async (file) => {
    if (!file) return;
    if (atLimit) {
      toast.error(`${membership} plan allows up to ${limit} banners. Delete one or upgrade.`);
      return;
    }
    if (!/\.jpe?g$/i.test(file.name) && file.type !== "image/jpeg") {
      toast.error("Banners must be JPG files");
      return;
    }
    // Client-side dimension check for a snappier UX; backend re-validates.
    const dimsOk = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth === BANNER_W && img.naturalHeight === BANNER_H);
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
    if (!dimsOk) {
      toast.error(`Banner must be exactly ${BANNER_W}×${BANNER_H} px`);
      return;
    }
    if (!targetUrl.trim() || !/^https?:\/\//i.test(targetUrl.trim())) {
      toast.error("Enter a valid target URL (must start with http:// or https://)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target_url", targetUrl.trim());
      await api.post("/banners", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner uploaded");
      setTargetUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (b) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/banners/${b.id}`);
      toast.success("Banner deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Advertising</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            My Banners
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Upload 728×90 JPG banners. Impressions are added to your account by an admin after purchase.
          </p>
        </div>

        {/* Impressions balance */}
        <div data-testid="my-banners-balance" className="rounded-2xl border border-fuchsia-200 dark:border-fuchsia-500/30 bg-gradient-to-br from-white to-fuchsia-50 dark:from-slate-900 dark:to-slate-900 p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Available impressions</div>
            <div className="mt-1 text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              {balance.toLocaleString()}
            </div>
          </div>
          <a
            href="/buy-banners"
            data-testid="my-banners-buy-more"
            className="inline-flex items-center gap-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black uppercase tracking-widest text-sm px-4 py-2"
          >
            <Flag size={14} weight="fill" /> Buy more <ArrowSquareOut size={12} weight="bold" />
          </a>
        </div>

        {/* Upload */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Upload a new banner</div>
            <span
              data-testid="my-banners-slot-usage"
              className={`inline-flex items-center gap-1 rounded-full text-[10px] font-black uppercase tracking-widest px-2.5 py-1 ${
                atLimit
                  ? "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
              }`}
              title={`${membership} plan allows up to ${limit} banners`}
            >
              {banners.length} / {limit} slots · {membership}
            </span>
          </div>
          <div className="grid md:grid-cols-[1fr_auto] gap-3">
            <input
              data-testid="my-banners-target-url"
              type="url"
              placeholder="Target URL (https://…)"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            />
            <label
              data-testid="my-banners-upload-btn"
              className={`inline-flex items-center justify-center gap-1 cursor-pointer rounded-xl text-sm font-bold px-4 py-2 whitespace-nowrap text-white ${
                atLimit || uploading
                  ? "bg-slate-400 dark:bg-slate-700 cursor-not-allowed pointer-events-none"
                  : "bg-blue-700 hover:bg-blue-800"
              }`}
            >
              <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : atLimit ? "Slots full" : "Upload 728×90 JPG"}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                className="hidden"
                disabled={atLimit || uploading}
                onChange={(e) => validateAndUpload(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1">
            <Warning size={14} weight="bold" className="text-amber-500 mt-0.5" />
            <span>Only <strong>JPG</strong> files at exactly <strong>{BANNER_W}×{BANNER_H} px</strong>. Files up to 2MB.</span>
          </div>
          {atLimit && (
            <div className="mt-3 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
              You've hit the {membership} banner limit ({limit}). Delete one below, or {" "}
              <a href="/buy-upgrades" className="font-bold underline">upgrade your plan</a> to unlock more slots (Silver 10, Gold 20, Platinum 40).
            </div>
          )}
        </section>

        {/* Banner list */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Your banners ({banners.length})</div>
          {loading ? (
            <div className="py-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : banners.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No banners yet — upload one above.</div>
          ) : (
            <div className="space-y-3">
              {banners.map((b) => (
                <div key={b.id} data-testid={`banner-row-${b.id}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 flex items-center gap-4 flex-wrap">
                  <img
                    src={b.image_url}
                    alt="banner"
                    className="rounded-md bg-white"
                    style={{ width: BANNER_W / 2, height: BANNER_H / 2, objectFit: "contain" }}
                  />
                  <div className="flex-1 min-w-0">
                    <a
                      href={b.target_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm font-bold text-blue-700 dark:text-blue-400 hover:underline truncate block"
                      title={b.target_url}
                    >
                      {b.target_url}
                    </a>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                        <Eye size={11} weight="bold" /> {Number(b.impressions_served || 0).toLocaleString()} views
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                        <CursorClick size={11} weight="bold" /> {Number(b.clicks_served || 0).toLocaleString()} clicks
                      </span>
                      {b.impressions_served > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                          CTR {((Number(b.clicks_served || 0) / Math.max(1, Number(b.impressions_served || 0))) * 100).toFixed(2)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`banner-delete-${b.id}`}
                    onClick={() => remove(b)}
                    className="inline-flex items-center gap-1 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 text-xs font-bold px-3 py-1.5 hover:bg-rose-200 dark:hover:bg-rose-500/30"
                  >
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
